import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toIsoLocal } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Plus,
  Search,
  ArrowRight,
  Calendar,
  Download,
  Users,
  Eye,
  EyeOff,
  CheckCircle2,
  Loader2,
  FileSpreadsheet,
  UserCheck,
  AlertCircle,
  Palmtree
} from 'lucide-react';
import { toast } from 'sonner';
import { useT, useLang } from '@/i18n';

const LOCALE_MAP = { en: 'en-GB', es: 'es-ES', fi: 'fi-FI', sv: 'sv-SE', de: 'de-DE' };

const BookingsPage = () => {
  const t = useT();
  const [lang] = useLang();
  const loc = LOCALE_MAP[lang] || 'en-GB';
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('');
  const [checkinDateFrom, setCheckinDateFrom] = useState('');
  const [checkinDateTo, setCheckinDateTo] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  const [formData, setFormData] = useState({
    property_id: '',
    start_date: '',
    end_date: '',
    guest_first_name: '',
    guest_last_name: '',
    guest_email: '',
    guest_phone: '',
    guest_id_type: 'passport',
    guest_id_number: '',
    guest_nationality: '',
    guest_billing_address: '',
    is_owner_reservation: false,
    additional_guests: []
  });

  // Owner reservation auto-fill state
  const [ownerContact, setOwnerContact] = useState(null);
  const [ownerContactLoading, setOwnerContactLoading] = useState(false);

  // Availability calendar state
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState({ from: undefined, to: undefined });
  const [availableProperties, setAvailableProperties] = useState([]);
  
  // Export dialog state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportFields, setExportFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [exportDateType, setExportDateType] = useState('checkin');
  const [exportDateFrom, setExportDateFrom] = useState('');
  const [exportDateTo, setExportDateTo] = useState('');
  const [exportFormat, setExportFormat] = useState('xlsx');

  const isAdminOrCS = ['ADMIN', 'CUSTOMER_SERVICE'].includes(user?.role);
  const isOwner = user?.role === 'OWNER';
  const isCustomer = user?.role === 'CUSTOMER';


  // Owner export state
  const [showOwnerExportDialog, setShowOwnerExportDialog] = useState(false);
  const [ownerExportFrom, setOwnerExportFrom] = useState('');
  const [ownerExportTo, setOwnerExportTo] = useState('');
  const [ownerExportFormat, setOwnerExportFormat] = useState('xlsx');
  const [ownerExportLoading, setOwnerExportLoading] = useState(false);
  const [ownerExportPreview, setOwnerExportPreview] = useState(null); // { count, total_net, total_accom }

  useEffect(() => {
    fetchBookings();
    fetchProperties();
  }, [search, statusFilter, propertyFilter, checkinDateFrom, checkinDateTo]);

  const fetchBookings = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (propertyFilter) params.property_id = propertyFilter;
      if (checkinDateFrom) params.checkin_from = checkinDateFrom;
      if (checkinDateTo) params.checkin_to = checkinDateTo;
      
      const response = await axios.get(`${API}/bookings`, { params });
      setBookings(response.data.items || []);
    } catch (error) {
      toast.error(t('bookings.page.toast_load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data.items || []);
    } catch (error) {
      console.error('Failed to load properties');
    }
  };

  // Fetch available properties for selected dates
  const fetchAvailableProperties = async (startDate, endDate) => {
    if (!startDate || !endDate) {
      setAvailableProperties([]);
      return;
    }
    
    setCalendarLoading(true);
    try {
      // Check availability for each property
      const availableList = [];
      for (const prop of properties) {
        try {
          const response = await axios.get(`${API}/calendar/availability/${prop.id}`, {
            params: { start_date: startDate, end_date: endDate }
          });
          // Check is_available (not available)
          if (response.data.is_available) {
            availableList.push(prop);
          }
        } catch (error) {
          // If check fails, skip this property
          console.error(`Failed to check availability for ${prop.id}:`, error);
        }
      }
      setAvailableProperties(availableList);
    } catch (error) {
      console.error('Failed to check availability:', error);
      setAvailableProperties([]);
    } finally {
      setCalendarLoading(false);
    }
  };

  // Handle date range selection from calendar - triggers availability check
  const handleDateRangeSelect = (range) => {
    // Ensure range is always an object with from/to properties
    const safeRange = range || { from: undefined, to: undefined };
    setSelectedDateRange(safeRange);
    
    if (safeRange.from && safeRange.to) {
      const startDate = toIsoLocal(safeRange.from);
      const endDate = toIsoLocal(safeRange.to);
      setFormData({ ...formData, start_date: startDate, end_date: endDate, property_id: '' });
      fetchAvailableProperties(startDate, endDate);
    } else {
      setAvailableProperties([]);
      setFormData({ ...formData, start_date: '', end_date: '', property_id: '' });
    }
  };

  // Fetch property owner contact for auto-fill
  const fetchOwnerContact = async (propertyId) => {
    if (!propertyId) return;
    setOwnerContactLoading(true);
    try {
      const resp = await axios.get(`${API}/properties/${propertyId}/owner-contact`);
      const contact = resp.data;
      setOwnerContact(contact);
      // Split name into first / last
      const parts = (contact.name || '').trim().split(' ');
      const firstName = parts[0] || '';
      const lastName = parts.slice(1).join(' ') || '';
      setFormData(prev => ({
        ...prev,
        guest_first_name: firstName,
        guest_last_name: lastName,
        guest_email: contact.email || '',
        guest_phone: contact.phone || '',
      }));
    } catch {
      toast.error(t('bookings.page.toast_owner_fetch_failed'));
      setOwnerContact(null);
    } finally {
      setOwnerContactLoading(false);
    }
  };

  // Clear guest fields that were auto-filled by owner reservation
  const clearOwnerFields = () => {
    setOwnerContact(null);
    setFormData(prev => ({
      ...prev,
      guest_first_name: '',
      guest_last_name: '',
      guest_email: '',
      guest_phone: '',
    }));
  };

  // Handle property selection after dates are chosen
  const handlePropertySelect = (propertyId) => {
    setFormData(prev => ({ ...prev, property_id: propertyId }));
    // If owner reservation is already ticked, auto-fill with new property owner
    if (formData.is_owner_reservation) {
      fetchOwnerContact(propertyId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Client-side required-field validation (billing address is optional)
    const required = [
      { key: 'guest_first_name', msg: t('bookings.page.required_first') },
      { key: 'guest_last_name',  msg: t('bookings.page.required_last') },
      { key: 'guest_email',      msg: t('bookings.page.required_email') },
      { key: 'guest_phone',      msg: t('bookings.page.required_phone') },
      { key: 'guest_id_type',    msg: t('bookings.page.required_id_type') },
      { key: 'guest_id_number',  msg: t('bookings.page.required_id_number') },
    ];
    for (const field of required) {
      if (!formData[field.key]?.trim()) {
        toast.error(field.msg);
        return;
      }
    }
    try {
      await axios.post(`${API}/bookings`, formData);
      toast.success(t('bookings.page.toast_created'));
      setShowCreateDialog(false);
      resetForm();
      fetchBookings();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.message) {
        toast.error(detail.message);
      } else {
        toast.error(detail || t('bookings.page.toast_create_failed'));
      }
    }
  };

  const resetForm = () => {
    setFormData({
      property_id: '',
      start_date: '',
      end_date: '',
      guest_first_name: '',
      guest_last_name: '',
      guest_email: '',
      guest_phone: '',
      guest_id_type: 'passport',
      guest_id_number: '',
      guest_nationality: '',
      guest_billing_address: '',
      is_owner_reservation: false,
      additional_guests: []
    });
    setSelectedDateRange({ from: undefined, to: undefined });
    setAvailableProperties([]);
    setOwnerContact(null);
  };

  // Fetch available export fields when export dialog opens
  const openExportDialog = async () => {
    setShowExportDialog(true);
    try {
      const response = await axios.get(`${API}/export/bookings/fields`);
      const fields = response.data.fields || [];
      setExportFields(fields);
      setSelectedFields(fields.map(f => f.id)); // Select all by default
    } catch (error) {
      console.error('Failed to load export fields:', error);
      toast.error(t('bookings.page.toast_export_options_failed'));
    }
  };

  const toggleFieldSelection = (fieldId) => {
    setSelectedFields(prev => 
      prev.includes(fieldId) 
        ? prev.filter(f => f !== fieldId)
        : [...prev, fieldId]
    );
  };

  const selectAllFields = () => {
    setSelectedFields(exportFields.map(f => f.id));
  };

  const deselectAllFields = () => {
    setSelectedFields([]);
  };

  const handleExport = async () => {
    if (selectedFields.length === 0) {
      toast.error(t('bookings.page.toast_select_field'));
      return;
    }

    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('format', exportFormat);
      params.append('date_type', exportDateType);
      if (exportDateFrom) params.append('date_from', exportDateFrom);
      if (exportDateTo) params.append('date_to', exportDateTo);
      if (propertyFilter) params.append('property_id', propertyFilter);
      if (statusFilter) params.append('status', statusFilter);
      params.append('fields', selectedFields.join(','));

      // Use fetch for blob download
      const response = await fetch(`${API}/export/bookings?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bookings_export.${exportFormat}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(t('bookings.page.toast_exported'));
      setShowExportDialog(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('bookings.page.toast_export_failed'));
    } finally {
      setExportLoading(false);
    }
  };

  // Owner export: fetch preview (count + accommodation total)
  const handleOwnerPreview = async () => {
    if (!ownerExportFrom || !ownerExportTo) return;
    setOwnerExportLoading(true);
    try {
      const res = await axios.get(`${API}/bookings`, {
        params: { checkin_from: ownerExportFrom, checkin_to: ownerExportTo }
      });
      const bks = res.data.items || [];
      const totalAccom = bks.reduce((sum, b) => sum + (b.price_breakdown?.accommodation_subtotal || 0), 0);
      setOwnerExportPreview({ count: bks.length, total_accom: totalAccom });
    } catch {
      toast.error(t('bookings.page.toast_preview_failed'));
    } finally {
      setOwnerExportLoading(false);
    }
  };

  // Owner export: download file
  const handleOwnerExport = async () => {
    if (!ownerExportFrom || !ownerExportTo) {
      toast.error(t('bookings.page.toast_select_range'));
      return;
    }
    setOwnerExportLoading(true);
    try {
      const fields = [
        'reference', 'property_title', 'start_date', 'end_date',
        'nights', 'status', 'is_owner_reservation',
        'accommodation_subtotal', 'owner_net_amount'
      ].join(',');
      const params = new URLSearchParams({
        format: ownerExportFormat,
        fields,
        date_from: ownerExportFrom,
        date_to: ownerExportTo,
        date_type: 'checkin',
      });
      const response = await fetch(`${API}/export/bookings?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `my_bookings_${ownerExportFrom}_${ownerExportTo}.${ownerExportFormat}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('bookings.page.toast_exported'));
      setShowOwnerExportDialog(false);
    } catch {
      toast.error(t('bookings.page.toast_export_failed'));
    } finally {
      setOwnerExportLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      PENDING: 'badge-warning',
      CONFIRMED: 'badge-success',
      CANCELLED: 'badge-error',
      COMPLETED: 'badge-success'
    };
    const labels = {
      PENDING: t('bookings.status_pending'),
      CONFIRMED: t('bookings.status_confirmed'),
      CANCELLED: t('bookings.status_cancelled'),
      COMPLETED: t('bookings.status_completed'),
    };
    return <span className={styles[status] || 'badge-info'}>{labels[status] || status}</span>;
  };

  const getPaymentBadge = (status) => {
    const styles = {
      DEPOSIT_DUE: 'badge-warning',
      DEPOSIT_PAID: 'badge-info',
      BALANCE_DUE: 'badge-warning',
      BALANCE_PAID: 'badge-success',
      REFUNDED: 'badge-error'
    };
    const labels = {
      DEPOSIT_DUE: t('bookings.payment_deposit_due'),
      DEPOSIT_PAID: t('bookings.payment_deposit_paid'),
      BALANCE_DUE: t('bookings.payment_balance_due'),
      BALANCE_PAID: t('bookings.payment_balance_paid'),
      REFUNDED: t('bookings.payment_refunded'),
    };
    return <span className={styles[status] || 'badge-info'}>{labels[status] || status?.replace(/_/g, ' ')}</span>;
  };

  // Get guest display info - respects RBAC (owners don't see guest info).
  // Count comes from booking.num_guests (what the customer picked on the
  // public booking form) rather than booking.guests.length — the per-guest
  // array only has records for guests whose details were explicitly entered,
  // which is typically just the primary. A party of 5 would otherwise show
  // as "no badge" because guestInfo.count > 1 was false.
  const getGuestDisplay = (booking) => {
    if (booking.guests && booking.guests.length > 0) {
      const primary = booking.guests.find(g => g.is_primary) || booking.guests[0];
      return {
        name: `${primary.first_name} ${primary.last_name}`,
        email: primary.email,
        count: booking.num_guests ?? booking.guests.length
      };
    }
    // For OWNER role - no guest info available (RBAC redaction)
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bookings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0c2431] font-['Outfit']">
            {isOwner ? t('bookings.page.title_reservations') : t('bookings.page.title_bookings')}
          </h1>
          <p className="text-[#194a50]/70 mt-1">{t('bookings.page.count_found', { n: bookings.length })}</p>
          {isOwner && (
            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
              <EyeOff className="w-3 h-3" />
              {t('bookings.page.guest_details_hidden')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {isOwner && (
            <Button
              variant="outline"
              onClick={() => { setOwnerExportPreview(null); setShowOwnerExportDialog(true); }}
              className="border-[#194a50]/20"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('bookings.page.export_my_bookings')}
            </Button>
          )}
          {isCustomer && (
            <Button
              className="bg-[#194a50] hover:bg-[#10555e] rounded-full"
              onClick={() => navigate('/browse')}
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('bookings.page.book_a_stay')}
            </Button>
          )}
          {isAdminOrCS && (
            <>
              <Button variant="outline" onClick={openExportDialog} className="border-[#194a50]/20" data-testid="export-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                {t('bookings.page.export')}
              </Button>
              <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-[#194a50] hover:bg-[#10555e] rounded-full" data-testid="create-booking-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('bookings.page.new_booking')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-['Outfit']">{t('bookings.page.create_new_booking')}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      
                      {/* Step 1: Select Dates First */}
                      <div className="col-span-2 border rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <Label className="text-base font-semibold">{t('bookings.page.step1_title')}</Label>
                            <p className="text-xs text-[#194a50]/60">{t('bookings.page.step1_subtitle')}</p>
                          </div>
                          {calendarLoading && (
                            <Loader2 className="w-5 h-5 animate-spin text-[#194a50]" />
                          )}
                        </div>
                        
                        <div className="flex justify-center">
                          <CalendarComponent
                            mode="range"
                            selected={selectedDateRange}
                            onSelect={handleDateRangeSelect}
                            numberOfMonths={2}
                            disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                            className="rounded-md border bg-white"
                            data-testid="booking-date-calendar"
                          />
                        </div>
                        
                        {/* Selected Dates Display */}
                        {selectedDateRange?.from && selectedDateRange?.to && (
                          <div className="mt-3 text-sm text-[#194a50] text-center">
                            <span className="font-medium">{t('bookings.page.selected_label')} </span>
                            {selectedDateRange.from.toLocaleDateString(loc)} - {selectedDateRange.to.toLocaleDateString(loc)}
                            <span className="text-[#194a50]/60 ml-2">
                              {t('bookings.page.selected_nights', { n: Math.ceil((selectedDateRange.to - selectedDateRange.from) / (1000 * 60 * 60 * 24)) })}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Step 2: Select Property - Shows after dates selected */}
                      {selectedDateRange?.from && selectedDateRange?.to && (
                        <div className="col-span-2 border rounded-lg p-4">
                          <Label className="text-base font-semibold">{t('bookings.page.step2_title')}</Label>
                          <p className="text-xs text-[#194a50]/60 mb-3">
                            {availableProperties.length > 0
                              ? t('bookings.page.step2_available', { n: availableProperties.length })
                              : calendarLoading
                                ? t('bookings.page.step2_checking')
                                : t('bookings.page.step2_none')
                            }
                          </p>
                          
                          {availableProperties.length > 0 && (
                            <div className="grid gap-2">
                              {availableProperties.map((prop) => (
                                <div 
                                  key={prop.id}
                                  className={`p-3 border rounded-lg cursor-pointer transition-all ${
                                    formData.property_id === prop.id 
                                      ? 'border-[#194a50] bg-[#194a50]/5' 
                                      : 'border-gray-200 hover:border-[#194a50]/50'
                                  }`}
                                  onClick={() => handlePropertySelect(prop.id)}
                                  data-testid={`property-option-${prop.id}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-medium text-[#0c2431]">{prop.title}</p>
                                      <p className="text-sm text-[#194a50]/60">
                                        {prop.reference && <span className="mr-2">{prop.reference}</span>}
                                        {prop.location?.city}, {prop.location?.country}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-semibold text-[#194a50]">€{prop.base_nightly_rate}{t('bookings.page.per_night')}</p>
                                      {formData.property_id === prop.id && (
                                        <CheckCircle2 className="w-5 h-5 text-green-600 ml-auto mt-1" />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Hidden inputs to maintain form data */}
                      <input type="hidden" name="start_date" value={formData.start_date} />
                      <input type="hidden" name="end_date" value={formData.end_date} />
                      
                      {/* ── Primary Guest Information ─────────────── */}
                      <div className="col-span-2 border-t pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-[#0c2431]">{t('bookings.page.primary_guest_info')}</h3>
                          <span className="text-xs text-[#194a50]/60 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {t('bookings.page.required_note')} <span className="text-red-500 font-bold mx-0.5">*</span> {t('bookings.page.required_note_end')}
                          </span>
                        </div>

                        {/* Owner Reservation toggle — FIRST, before fields */}
                        <div className={`flex items-center gap-3 p-3 rounded-lg border mb-4 cursor-pointer transition-all ${
                          formData.is_owner_reservation
                            ? 'bg-[#194a50]/5 border-[#194a50]/40'
                            : 'bg-gray-50 border-gray-200 hover:border-[#194a50]/30'
                        }`}
                          onClick={() => {
                            const next = !formData.is_owner_reservation;
                            setFormData(prev => ({ ...prev, is_owner_reservation: next }));
                            if (next && formData.property_id) {
                              fetchOwnerContact(formData.property_id);
                            } else if (!next) {
                              clearOwnerFields();
                            }
                          }}
                          data-testid="owner-reservation-checkbox"
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            formData.is_owner_reservation
                              ? 'bg-[#194a50] border-[#194a50]'
                              : 'border-gray-300 bg-white'
                          }`}>
                            {formData.is_owner_reservation && (
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-[#0c2431]">{t('bookings.page.owner_reservation')} <span className="text-[#194a50]/50 font-normal">{t('bookings.page.no_platform_fee')}</span></p>
                            <p className="text-xs text-[#194a50]/60 mt-0.5">
                              {formData.is_owner_reservation
                                ? formData.property_id
                                  ? ownerContactLoading
                                    ? t('bookings.page.owner_loading')
                                    : ownerContact
                                      ? t('bookings.page.owner_autofilled_short')
                                      : t('bookings.page.owner_select_property')
                                  : t('bookings.page.owner_select_above')
                                : t('bookings.page.owner_check_hint')
                              }
                            </p>
                          </div>
                          {formData.is_owner_reservation && ownerContactLoading && (
                            <Loader2 className="w-4 h-4 animate-spin text-[#194a50]" />
                          )}
                          {formData.is_owner_reservation && ownerContact && !ownerContactLoading && (
                            <UserCheck className="w-4 h-4 text-green-600" />
                          )}
                        </div>

                        {/* Auto-filled banner */}
                        {formData.is_owner_reservation && ownerContact && (
                          <div className="flex items-center gap-2 px-3 py-2 mb-3 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700">
                            <UserCheck className="w-3.5 h-3.5 flex-shrink-0" />
                            {t('bookings.page.owner_autofilled_banner')}
                          </div>
                        )}
                      </div>

                      {/* Guest fields */}
                      <div>
                        <Label className="flex items-center gap-1">
                          {t('bookings.page.first_name')} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={formData.guest_first_name}
                          onChange={(e) => setFormData({ ...formData, guest_first_name: e.target.value })}
                          placeholder={t('bookings.page.placeholder_first')}
                          data-testid="guest-first-name-input"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-1">
                          {t('bookings.page.last_name')} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={formData.guest_last_name}
                          onChange={(e) => setFormData({ ...formData, guest_last_name: e.target.value })}
                          placeholder={t('bookings.page.placeholder_last')}
                          data-testid="guest-last-name-input"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-1">
                          {t('bookings.page.email_label')} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          type="email"
                          value={formData.guest_email}
                          onChange={(e) => setFormData({ ...formData, guest_email: e.target.value })}
                          placeholder={t('bookings.page.placeholder_email')}
                          data-testid="guest-email-input"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-1">
                          {t('bookings.page.phone_label')} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={formData.guest_phone}
                          onChange={(e) => setFormData({ ...formData, guest_phone: e.target.value })}
                          placeholder={t('bookings.page.placeholder_phone')}
                          data-testid="guest-phone-input"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-1">
                          {t('bookings.page.id_type_label')} <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={formData.guest_id_type}
                          onValueChange={(val) => setFormData({ ...formData, guest_id_type: val })}
                        >
                          <SelectTrigger data-testid="id-type-select">
                            <SelectValue placeholder={t('bookings.page.select_id_type')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="passport">{t('bookings.page.id_passport')}</SelectItem>
                            <SelectItem value="national_id">{t('bookings.page.id_national')}</SelectItem>
                            <SelectItem value="driver_license">{t('bookings.page.id_driver')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="flex items-center gap-1">
                          {t('bookings.page.id_number_label')} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={formData.guest_id_number}
                          onChange={(e) => setFormData({ ...formData, guest_id_number: e.target.value })}
                          placeholder={t('bookings.page.placeholder_id')}
                          data-testid="guest-id-input"
                        />
                      </div>
                      <div>
                        <Label>{t('bookings.page.nationality_label')}</Label>
                        <Input
                          value={formData.guest_nationality}
                          onChange={(e) => setFormData({ ...formData, guest_nationality: e.target.value })}
                          placeholder={t('bookings.page.placeholder_nationality')}
                          data-testid="guest-nationality-input"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="flex items-center gap-1">
                          {t('bookings.page.billing_address')}
                          <span className="text-[#194a50]/40 text-xs font-normal ml-1">{t('bookings.page.optional')}</span>
                        </Label>
                        <Input
                          value={formData.guest_billing_address}
                          onChange={(e) => setFormData({ ...formData, guest_billing_address: e.target.value })}
                          placeholder={t('bookings.page.placeholder_billing')}
                          data-testid="billing-address-input"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
                        {t('bookings.page.cancel')}
                      </Button>
                      <Button type="submit" className="bg-[#194a50] hover:bg-[#10555e]" data-testid="submit-booking-btn">
                        {t('bookings.page.create_booking_btn')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="shadow-sm border-[#194a50]/5">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#194a50]/50" />
              <Input
                placeholder={t('bookings.page.search_placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 border-[#194a50]/20"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5 border border-[#194a50]/10">
              <Label className="text-xs text-[#194a50]/60 whitespace-nowrap">{t('bookings.page.checkin_label')}</Label>
              <Input
                type="date"
                value={checkinDateFrom}
                onChange={(e) => setCheckinDateFrom(e.target.value)}
                className="w-36 h-8 border-[#194a50]/20 text-sm"
                data-testid="checkin-date-from"
              />
              <span className="text-[#194a50]/40">-</span>
              <Input
                type="date"
                value={checkinDateTo}
                onChange={(e) => setCheckinDateTo(e.target.value)}
                className="w-36 h-8 border-[#194a50]/20 text-sm"
                data-testid="checkin-date-to"
              />
              {(checkinDateFrom || checkinDateTo) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => { setCheckinDateFrom(''); setCheckinDateTo(''); }}
                  className="h-6 w-6 p-0 text-[#194a50]/50 hover:text-[#194a50]"
                >
                  ✕
                </Button>
              )}
            </div>
            <Select value={statusFilter || "all"} onValueChange={(val) => setStatusFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder={t('bookings.page.all_status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('bookings.page.all_status')}</SelectItem>
                <SelectItem value="PENDING">{t('bookings.status_pending')}</SelectItem>
                <SelectItem value="CONFIRMED">{t('bookings.status_confirmed')}</SelectItem>
                <SelectItem value="CANCELLED">{t('bookings.status_cancelled')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={propertyFilter || "all"} onValueChange={(val) => setPropertyFilter(val === "all" ? "" : val)}>
              <SelectTrigger className="w-48" data-testid="property-filter">
                <SelectValue placeholder={t('bookings.page.all_properties')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('bookings.page.all_properties')}</SelectItem>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.reference || prop.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card className="shadow-sm border-[#194a50]/5" data-testid="bookings-table-card">
        <CardContent className="p-0">
          {bookings.length === 0 ? (
            <div className="py-16 text-center">
              <BookOpen className="w-16 h-16 mx-auto mb-4 text-[#194a50]/30" />
              <h3 className="text-xl font-semibold text-[#0c2431] mb-2">{t('bookings.page.no_bookings_title')}</h3>
              <p className="text-[#194a50]/60">
                {search || statusFilter || propertyFilter || checkinDateFrom || checkinDateTo ? t('bookings.page.adjust_filters') : t('bookings.page.create_first')}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('bookings.page.col_reference')}</th>
                    <th>{t('bookings.page.col_property')}</th>
                    {!isOwner && <th>{t('bookings.page.col_guest')}</th>}
                    <th>{t('bookings.page.col_dates')}</th>
                    <th>{t('bookings.page.col_nights')}</th>
                    <th>{t('bookings.page.col_status')}</th>
                    {!isOwner && <th>{t('bookings.page.col_payment')}</th>}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => {
                    const guestInfo = getGuestDisplay(booking);
                    return (
                      <tr key={booking.id} data-testid={`booking-row-${booking.reference}`}>
                        <td className="font-medium text-[#0c2431]">
                          {booking.reference}
                          {booking.is_owner_reservation && (
                            <span className="ml-2 text-xs bg-[#d8a28c]/30 text-[#0c2431] px-2 py-0.5 rounded">{t('bookings.page.owner_badge')}</span>
                          )}
                        </td>
                        <td className="text-[#194a50]">{booking.property_title || t('bookings.page.na')}</td>
                        {!isOwner && (
                          <td>
                            {guestInfo ? (
                              <div>
                                <p className="text-[#0c2431] flex items-center gap-1">
                                  {guestInfo.name}
                                  {guestInfo.count > 1 && (
                                    <span className="text-xs text-[#194a50]/60 flex items-center">
                                      <Users className="w-3 h-3 mr-0.5" />+{guestInfo.count - 1}
                                    </span>
                                  )}
                                </p>
                                <p className="text-xs text-[#194a50]/60">{guestInfo.email}</p>
                              </div>
                            ) : (
                              <span className="text-[#194a50]/40 text-sm">-</span>
                            )}
                          </td>
                        )}
                        <td className="text-sm text-[#194a50]/70">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(booking.start_date).toLocaleDateString(loc, { day: 'numeric', month: 'short', year: 'numeric' })} - {new Date(booking.end_date).toLocaleDateString(loc, { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                        </td>
                        <td className="text-center text-[#194a50]">{booking.nights || '-'}</td>
                        <td>{getStatusBadge(booking.status)}</td>
                        {!isOwner && <td>{getPaymentBadge(booking.payment_status)}</td>}
                        <td>
                          <Link 
                            to={`/bookings/${booking.id}`}
                            className="text-[#194a50] hover:text-[#10555e]"
                            data-testid={`view-booking-${booking.reference}`}
                          >
                            <ArrowRight className="w-4 h-4" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Owner Export Dialog */}
      <Dialog open={showOwnerExportDialog} onOpenChange={(open) => { setShowOwnerExportDialog(open); if (!open) setOwnerExportPreview(null); }}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-['Outfit']">
              <FileSpreadsheet className="w-5 h-5 text-[#194a50]" />
              {t('bookings.page.owner_export_title')}
            </DialogTitle>
            <DialogDescription>
              {t('bookings.page.owner_export_description')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Date range */}
            <div className="space-y-3">
              <Label className="font-medium text-[#0c2431]">{t('bookings.page.date_range_checkin')}</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-[#194a50]/60">{t('bookings.page.from')}</Label>
                  <Input
                    type="date"
                    value={ownerExportFrom}
                    onChange={(e) => { setOwnerExportFrom(e.target.value); setOwnerExportPreview(null); }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#194a50]/60">{t('bookings.page.to')}</Label>
                  <Input
                    type="date"
                    value={ownerExportTo}
                    onChange={(e) => { setOwnerExportTo(e.target.value); setOwnerExportPreview(null); }}
                  />
                </div>
              </div>
              {/* Quick presets */}
              <div className="flex flex-wrap gap-2">
                {[
                  { label: t('bookings.page.preset_this_year'), getRange: () => {
                    const y = new Date().getFullYear();
                    return { from: `${y}-01-01`, to: `${y}-12-31` };
                  }},
                  { label: t('bookings.page.preset_last_year'), getRange: () => {
                    const y = new Date().getFullYear() - 1;
                    return { from: `${y}-01-01`, to: `${y}-12-31` };
                  }},
                  { label: t('bookings.page.preset_next_6'), getRange: () => {
                    const now = new Date();
                    const later = new Date(now); later.setMonth(later.getMonth() + 6);
                    return { from: toIsoLocal(now), to: toIsoLocal(later) };
                  }},
                  { label: t('bookings.page.preset_all_time'), getRange: () => ({ from: '2020-01-01', to: '2099-12-31' }) },
                ].map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => { const r = preset.getRange(); setOwnerExportFrom(r.from); setOwnerExportTo(r.to); setOwnerExportPreview(null); }}
                    className="text-xs px-2.5 py-1 rounded-full border border-[#194a50]/20 text-[#194a50] hover:bg-[#194a50]/5 transition-colors"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Format */}
            <div className="space-y-2">
              <Label className="font-medium text-[#0c2431]">{t('bookings.page.export_format')}</Label>
              <div className="flex gap-3">
                {[{ value: 'xlsx', label: t('bookings.page.excel_format') }, { value: 'csv', label: t('bookings.page.csv_format') }].map((fmt) => (
                  <button
                    key={fmt.value}
                    type="button"
                    onClick={() => setOwnerExportFormat(fmt.value)}
                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                      ownerExportFormat === fmt.value
                        ? 'border-[#194a50] bg-[#194a50] text-white'
                        : 'border-[#194a50]/20 text-[#194a50] hover:bg-[#194a50]/5'
                    }`}
                  >
                    {fmt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            {ownerExportFrom && ownerExportTo && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="font-medium text-[#0c2431]">{t('bookings.page.preview')}</Label>
                  <button
                    type="button"
                    onClick={handleOwnerPreview}
                    disabled={ownerExportLoading}
                    className="text-xs text-[#194a50] underline hover:text-[#10555e] disabled:opacity-50"
                  >
                    {ownerExportLoading ? t('bookings.page.loading') : t('bookings.page.load_preview')}
                  </button>
                </div>
                {ownerExportPreview ? (
                  <div className="bg-[#f4f7f8] rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#194a50]/60">{t('bookings.page.bookings_found_label')}</span>
                      <span className="font-semibold text-[#0c2431]">{ownerExportPreview.count}</span>
                    </div>
                    {ownerExportPreview.total_accom > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-[#194a50]/60">{t('bookings.page.total_accommodation')}</span>
                        <span className="font-semibold text-[#0c2431]">€{ownerExportPreview.total_accom.toFixed(2)}</span>
                      </div>
                    )}
                    <p className="text-xs text-[#194a50]/50 pt-1 border-t border-[#194a50]/10">
                      {t('bookings.page.net_note')}
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-[#194a50]/50 bg-[#f4f7f8] rounded-lg p-3">
                    {t('bookings.page.preview_hint')}
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOwnerExportDialog(false)}>
              {t('bookings.page.cancel')}
            </Button>
            <Button
              onClick={handleOwnerExport}
              disabled={ownerExportLoading || !ownerExportFrom || !ownerExportTo}
              className="bg-[#194a50] hover:bg-[#10555e]"
            >
              {ownerExportLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('bookings.page.exporting')}</>
              ) : (
                <><Download className="w-4 h-4 mr-2" />{t('bookings.page.download')}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-[#194a50]" />
              {t('bookings.page.export_bookings')}
            </DialogTitle>
            <DialogDescription>
              {t('bookings.page.export_description')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Date Range Section */}
            <div className="space-y-4">
              <h3 className="font-medium text-[#0c2431]">{t('bookings.page.date_range_filter')}</h3>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('bookings.page.filter_by')}</Label>
                  <Select value={exportDateType} onValueChange={setExportDateType}>
                    <SelectTrigger data-testid="export-date-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="checkin">{t('bookings.page.checkin_date')}</SelectItem>
                      <SelectItem value="checkout">{t('bookings.page.checkout_date')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('bookings.page.from_date')}</Label>
                  <Input
                    type="date"
                    value={exportDateFrom}
                    onChange={(e) => setExportDateFrom(e.target.value)}
                    data-testid="export-date-from"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('bookings.page.to_date')}</Label>
                  <Input
                    type="date"
                    value={exportDateTo}
                    onChange={(e) => setExportDateTo(e.target.value)}
                    data-testid="export-date-to"
                  />
                </div>
              </div>
            </div>

            {/* Format Selection */}
            <div className="space-y-2">
              <Label>{t('bookings.page.export_format')}</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger className="w-48" data-testid="export-format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="xlsx">{t('bookings.page.excel_format')}</SelectItem>
                  <SelectItem value="csv">{t('bookings.page.csv_format')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Field Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-[#0c2431]">
                  {t('bookings.page.fields_to_export', { sel: selectedFields.length, tot: exportFields.length })}
                </h3>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={selectAllFields}
                    className="text-xs"
                  >
                    {t('bookings.page.select_all')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={deselectAllFields}
                    className="text-xs"
                  >
                    {t('bookings.page.deselect_all')}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-4 bg-gray-50 rounded-lg max-h-60 overflow-y-auto">
                {exportFields.map((field) => (
                  <div 
                    key={field.id} 
                    className="flex items-center space-x-2"
                  >
                    <Checkbox 
                      id={`field-${field.id}`}
                      checked={selectedFields.includes(field.id)}
                      onCheckedChange={() => toggleFieldSelection(field.id)}
                      data-testid={`field-${field.id}`}
                    />
                    <label 
                      htmlFor={`field-${field.id}`}
                      className="text-sm text-[#194a50] cursor-pointer select-none"
                    >
                      {field.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Current Filters Notice */}
            {(propertyFilter || statusFilter) && (
              <div className="text-sm text-[#194a50]/70 bg-amber-50 p-3 rounded-lg">
                <strong>{t('bookings.page.current_filters_note')}</strong> {t('bookings.page.current_filters_body')}
                {propertyFilter && <span className="ml-2 text-amber-700">{t('bookings.page.property_filter_active')}</span>}
                {statusFilter && <span className="ml-2 text-amber-700">{t('bookings.page.status_label', { status: statusFilter })}</span>}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowExportDialog(false)}
            >
              {t('bookings.page.cancel')}
            </Button>
            <Button
              onClick={handleExport}
              disabled={exportLoading || selectedFields.length === 0}
              className="bg-[#194a50] hover:bg-[#10555e]"
              data-testid="export-download-btn"
            >
              {exportLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('bookings.page.exporting')}
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  {t('bookings.page.download_export')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingsPage;
